import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../utils/api';
import { isAuthenticated, clearAuthData, getAuthData, getRefreshToken } from '../utils/auth';
import { User, UserLot } from '../types';

interface FetchUserOptions {
    silent?: boolean;
}

interface UserContextType {
    user: User | null;
    userLots: UserLot[];
    loading: boolean;
    error: string | null;
    isServerOffline: boolean;
    fetchUserData: (options?: FetchUserOptions) => Promise<void>;
    fetchUserLots: () => Promise<UserLot[]>;
    logout: () => Promise<void>;
    retryConnection: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

interface UserProviderProps {
    children: ReactNode;
}

const isArchivedLot = (lot: any): boolean => {
    const status = (lot?.lotStatus || lot?.accountStatus || "").toString().toLowerCase();
    return status === "suspended" || status === "archived";
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [userLots, setUserLots] = useState<UserLot[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isServerOffline, setIsServerOffline] = useState<boolean>(false);

    const fetchUserData = async (options: FetchUserOptions = {}): Promise<void> => {
        const { silent = false } = options;

        try {
            if (!silent) {
                setLoading(true);
                setError(null);
            }

            const authData = getAuthData();
            if (!authData) {
                console.log("No auth data available, skipping user fetch");
                if (!silent) {
                    setLoading(false);
                }
                return;
            }

            console.log("Fetching user data for userId:", authData.userId);

            const response = await api.get(`/users/get-user-by-id/${authData.userId}`);

            if (response.data && response.data.success && response.data.data) {
                const userData = response.data.data;
                console.log("User data fetched successfully:", userData);

                const normalizedUser = {
                    userId: userData.userId,
                    displayUserId: userData.displayUserId,
                    name: userData.name,
                    email: userData.email,
                    role: userData.role,
                    phoneNo: userData.phoneNo,
                    isVerified: userData.isVerified,
                    createdOn: userData.createdOn,
                    modifiedOn: userData.modifiedOn,
                    allowOperatorNotificationByEmail: userData.allowOperatorNotificationByEmail,
                };

                setUser(normalizedUser);
                try {
                    sessionStorage.setItem("parallel_user_role", (normalizedUser.role || "").toLowerCase());
                } catch (storageError) {
                    console.warn("Unable to cache user role for superadmin checks", storageError);
                }

                setIsServerOffline(false);
            } else {
                throw new Error("Invalid user data response");
            }
        } catch (err: any) {
            console.error("Error fetching user data:", err);

            if (!err.response) {
                // Network error
                setIsServerOffline(true);
                if (!silent) {
                    setError("Server is offline. Please check your connection.");
                }
            } else if (err.response.status === 401) {
                // Unauthorized - clear auth data and let the auth system handle it
                console.log("Unauthorized error, clearing auth data");
                clearAuthData();
                setUser(null);
                if (!silent) {
                    setError("Authentication expired. Please log in again.");
                }
            } else if (err.response.status === 404) {
                if (!silent) {
                    setError("User not found. Please contact support.");
                }
            } else {
                if (!silent) {
                    setError(err.response.data?.message || "Failed to fetch user data");
                }
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    const fetchUserLots = async (): Promise<UserLot[]> => {
        try {
            const authData = getAuthData();
            if (!authData) {
                console.log("No auth data available, skipping lots fetch");
                return [];
            }

            console.log("Fetching user lots for userId:", authData.userId);

            const response = await api.get(`/parkinglots/get-lots-by-user/${authData.userId}`);

            let lots: UserLot[] = [];

            if (response.data && response.data.success && response.data.data) {
                lots = response.data.data.filter((lot: any) => !isArchivedLot(lot)).map((lot: any) => ({
                    lotId: lot.lotId,
                    lotName: lot.lotName,
                    address: lot.address,
                    city: '', // Backend doesn't separate city/state/zip, using empty string
                    state: '',
                    zipCode: '',
                    role: 'Owner', // Default role - backend doesn't provide user role per lot
                    displayLotId: lot.displayLotId,
                    createdOn: lot.createdOn, // Include purchase date
                    lotStatus: lot.lotStatus, // Include lot status
                    companyName: lot.companyName,
                    lotCapacity: lot.lotCapacity,
                    currentOccupancy: lot.currentOccupancy,
                    lotLongitude: lot.lotLongitude,
                    lotLatitude: lot.lotLatitude,
                    developmentLot: lot.developmentLot === true,
                }));
            } else if (response.data && Array.isArray(response.data)) {
                // Handle direct array response
                lots = response.data.filter((lot: any) => !isArchivedLot(lot)).map((lot: any) => ({
                    lotId: lot.lotId,
                    lotName: lot.lotName,
                    address: lot.address,
                    city: '',
                    state: '',
                    zipCode: '',
                    role: 'Owner',
                    displayLotId: lot.displayLotId,
                    createdOn: lot.createdOn,
                    lotStatus: lot.lotStatus, // Include lot status
                    companyName: lot.companyName,
                    lotCapacity: lot.lotCapacity,
                    currentOccupancy: lot.currentOccupancy,
                    lotLongitude: lot.lotLongitude,
                    lotLatitude: lot.lotLatitude,
                    developmentLot: lot.developmentLot === true,
                }));
            }

            console.log("User lots fetched successfully:", lots);
            setUserLots(lots);
            setIsServerOffline(false);

            return lots;
        } catch (err: any) {
            console.error("Error fetching user lots:", err);

            if (!err.response) {
                // Network error
                setIsServerOffline(true);
                setError("Server is offline. Please check your connection.");
            } else if (err.response.status === 401) {
                // Unauthorized - clear auth data and let the auth system handle it
                console.log("Unauthorized error, clearing auth data");
                clearAuthData();
                setUser(null);
                setUserLots([]);
                setError("Authentication expired. Please log in again.");
            } else {
                setError(err.response.data?.message || "Failed to fetch user lots");
            }

            return [];
        }
    };

    const logout = async (): Promise<void> => {
        console.log("Logging out user");

        try {
            // Get the refresh token before clearing auth data
            const refreshToken = getRefreshToken();

            if (refreshToken) {
                console.log("Calling backend logout endpoint");
                // Call the backend logout endpoint to invalidate the refresh token
                await api.post('/login/logout', {
                    refreshToken: refreshToken
                });
                console.log("Backend logout successful");
            } else {
                console.warn("No refresh token found, skipping backend logout call");
            }
        } catch (error) {
            // Log error but continue with local logout
            console.error("Error calling backend logout endpoint:", error);
        }

        // Always clear local data regardless of backend call success
        clearAuthData();
        setUser(null);
        setUserLots([]);
        setError(null);
        try {
            sessionStorage.removeItem("parallel_user_role");
        } catch {
            // ignore
        }
        window.location.href = "/login";
    };

    const retryConnection = async (): Promise<void> => {
        console.log("Retrying connection...");
        setIsServerOffline(false);
        setError(null);

        if (isAuthenticated()) {
            await fetchUserData();
            await fetchUserLots();
        }
    };

    // Listen for server status events
    useEffect(() => {
        const handleServerStatus = (event: any) => {
            if (event.detail.status === 'offline') {
                setIsServerOffline(true);
                setError("Server is offline. Please check your connection.");
            } else if (event.detail.status === 'online') {
                setIsServerOffline(false);
                setError(null);
            }
        };

        window.addEventListener('server-status', handleServerStatus);
        return () => {
            window.removeEventListener('server-status', handleServerStatus);
        };
    }, []);

    // Initialize user data on mount
    useEffect(() => {
        const initializeUser = async () => {
            if (isAuthenticated()) {
                console.log("User is authenticated, fetching user data");
                await fetchUserData();
                await fetchUserLots();
            } else {
                console.log("User is not authenticated, skipping initialization");
                setLoading(false);
            }
        };

        initializeUser();
    }, []);

    const value: UserContextType = {
        user,
        userLots,
        loading,
        error,
        isServerOffline,
        fetchUserData,
        fetchUserLots,
        logout,
        retryConnection,
    };

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext; 
